"use client";

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { CountryCode } from "libphonenumber-js";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronDown, Search } from "lucide-react";
import { listCountries } from "@/lib/phone";
import { cn } from "@/lib/utils";
import { CountryFlag } from "./country-flag";

type Props = {
  value: CountryCode;
  onChange: (iso: CountryCode) => void;
  label: string;
};

/** Prefix stays on the physical left of the number. Hebrew RTL and narrow screens do not move it. */
export function PhoneDialRow({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div dir="ltr" className={cn("flex w-full min-w-0 flex-row flex-nowrap items-center gap-2", className)}>
      {children}
    </div>
  );
}

/** Country dial-code combobox (always LTR — it sits next to a phone input). */
export function CountryDialPicker({ value, onChange, label }: Props) {
  const countries = useMemo(() => listCountries(), []);
  const selected = countries.find((c) => c.iso === value);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return countries;
    const digits = q.replace(/\D/g, "");
    return countries.filter(
      (c) =>
        c.iso.toLowerCase().includes(q) ||
        c.dial.toLowerCase().includes(q) ||
        (digits && c.dial.replace(/\D/g, "").includes(digits)),
    );
  }, [countries, filter]);

  useEffect(() => {
    if (!open) return;
    setActiveIdx(Math.max(0, filtered.findIndex((c) => c.iso === value)));
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const el = listRef.current?.children[activeIdx] as HTMLElement | undefined;
    el?.scrollIntoView({ block: "nearest" });
  }, [activeIdx]);

  function pick(iso: CountryCode) {
    onChange(iso);
    setOpen(false);
    setFilter("");
  }

  function onKey(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter" && filtered[activeIdx]) {
      e.preventDefault();
      pick(filtered[activeIdx].iso);
    }
  }

  return (
    <div ref={rootRef} className="relative w-[9.25rem] shrink-0 sm:w-[11.5rem]" dir="ltr">
      <button
        type="button"
        className="input flex w-full items-center gap-2 text-left"
        onClick={() => setOpen((o) => !o)}
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        {selected ? (
          <>
            <CountryFlag iso={selected.iso} className="h-4 w-6 rounded-[2px]" />
            <span className="tabular min-w-0 flex-1 truncate text-sm font-medium">
              {selected.dial} <span className="font-normal text-ink-2">{selected.iso}</span>
            </span>
          </>
        ) : (
          <span>{label}</span>
        )}
        <ChevronDown className={cn("h-4 w-4 shrink-0 text-ink-2 transition-transform", open && "rotate-180")} aria-hidden />
      </button>
      <AnimatePresence>
        {open ? (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 mt-1 w-full min-w-[16rem] overflow-hidden rounded-xl border border-line bg-surface shadow-2"
            onKeyDown={onKey}
          >
            <div className="relative border-b border-line">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3" aria-hidden />
              <input
                className="h-11 w-full bg-transparent pl-9 pr-3 text-sm text-ink outline-none placeholder:text-ink-3"
                placeholder="IL / +972"
                lang="en"
                value={filter}
                onChange={(e) => {
                  setFilter(e.target.value);
                  setActiveIdx(0);
                }}
                aria-label={label}
                autoFocus
              />
            </div>
            <ul ref={listRef} role="listbox" aria-label={label} className="max-h-56 overflow-y-auto py-1">
              {filtered.map((c, i) => {
                const on = c.iso === value;
                return (
                  <li
                    key={c.iso}
                    role="option"
                    aria-selected={on}
                    onMouseEnter={() => setActiveIdx(i)}
                    onClick={() => pick(c.iso)}
                    className={cn(
                      "flex cursor-pointer items-center gap-2 px-3 py-2 text-sm",
                      i === activeIdx ? "bg-surface-2" : "",
                      on && "font-semibold",
                    )}
                  >
                    <CountryFlag iso={c.iso} className="h-4 w-6 rounded-[2px]" />
                    <span className="tabular">{c.dial}</span>
                    <span className="text-ink-2">{c.iso}</span>
                    {on ? <Check className="ml-auto h-4 w-4 text-brand" aria-hidden /> : null}
                  </li>
                );
              })}
              {filtered.length === 0 ? <li className="px-3 py-3 text-sm text-ink-3">—</li> : null}
            </ul>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
